/**
 * Every URL the frontend knows, in one place.
 *
 * Nothing outside this file may contain a literal API path. When the backend renames
 * a route this is the only file that changes.
 *
 * ---------------------------------------------------------------------------
 * TRAILING SLASHES — read this before adding a path
 * ---------------------------------------------------------------------------
 *
 * `APPEND_SLASH` is OFF on the backend. A near-miss is a hard 404, not a redirect,
 * because a redirected POST arrives as a GET with no body — which for a payment
 * verification is a data-loss bug rather than an inconvenience.
 *
 * There are TWO kinds of route and they differ:
 *
 *   ROUTER RESOURCES end in a slash: `cases/`, `cases/{id}/`, `cases/{id}/transition/`.
 *   The API client appends the slash automatically (`normalisePath` in `client.ts`),
 *   so these constants are written without one and cannot be got wrong at a call site.
 *
 *   REGISTERED PATHS (`registry.add_path`) are mounted exactly as written and take
 *   NO trailing slash: `/api/auth/staff/login`, `/api/staff/me`. Those live in
 *   `EXACT_PATHS` and MUST be requested with `{ trailingSlash: false }` — which the
 *   helpers in `auth.ts` already do. Do not call them by hand.
 */

export const API_ROOTS = {
  /** Router resources and the staff session routes. */
  staff: '/api/staff',
  portal: '/api/portal',
  platform: '/api/platform',
  link: '/api/link',
  root: '',
} as const;

/**
 * Paths mounted with `registry.add_path`, which take NO trailing slash.
 * Absolute — they are not relative to an audience root.
 */
export const EXACT_PATHS = {
  staffLogin: '/api/auth/staff/login',
  /**
   * The client's own login. A separate route on a separate audience: it issues a
   * `portal` token, which the staff root's authentication class refuses with a 401
   * before any view exists.
   */
  portalLogin: '/api/auth/portal/login',
  passwordResetRequest: '/api/auth/password/reset-request',
  passwordReset: '/api/auth/password/reset',
  invitationAccept: '/api/auth/invitations/accept',
  //: Who an invitation link is for, so the page can show the address filled in.
  invitationDetails: '/api/auth/invitations/details',
  //: The client equivalent. Separate because a contact has no membership, and the
  //: staff endpoint refuses a token without one.
  portalInvitationAccept: '/api/auth/portal-invitations/accept',

  /**
   * Redeem a single-purpose link into a session bound to ONE object.
   *
   * Not a login. It issues a `token` audience session that reaches exactly the object
   * the link was minted against and nothing else in the system, which is what makes it
   * safe to email to somebody who has no account -- and, before conversion, is not yet
   * a client of the firm.
   */
  linkRedeem: '/api/auth/link/redeem',

  /**
   * Self-serve demo sign-up. Public. Always answers the same way whether or not the
   * address already has an account, so it cannot be used to discover who does.
   */
  demoSignup: '/api/auth/demo/signup',

  /**
   * The client app's Android build, public. `appAndroid` describes the current build;
   * `appAndroidDownload` redirects to a ten-minute link to the file itself.
   */
  appAndroid: '/api/app/android',
  appAndroidDownload: '/api/app/android/download',

  staffMe: '/api/staff/me',
  staffRefresh: '/api/staff/auth/refresh',
  staffLogout: '/api/staff/auth/logout',
  staffLogoutAll: '/api/staff/auth/logout-all',
} as const;

/**
 * Router resources on the staff root. Relative to `API_ROOTS.staff`; the client
 * appends the trailing slash.
 *
 * ===========================================================================
 * LIVE ON THE BACKEND — build against these, not against mocks:
 *   cases (+ transition, team, responsible-lawyer, milestones,
 *          available-transitions), case notes, case updates, case timeline,
 *   tasks, documents, document-requests, agreements,
 *   invoices, payments, credit-notes, receipts, refunds, unallocated-receipts,
 *   financial-position, critical-dates, calendar-events,
 *   clients (+ contacts, consents, per-contact matter access),
 *   users, roles, departments, offices, organization, organization/branding,
 *   conversations (+ messages, read, addressable).
 *
 * NOT YET BUILT — still served by MSW:
 *   the rest of CRM, conflict, notifications. Each is marked below.
 * ===========================================================================
 */
export const STAFF = {
  /**
   * The read-only assistant. GET says whether it is switched on; POST asks a question.
   * Every look-up it makes is a staff API GET with the asker's own session.
   */
  assistant: '/assistant',
  // -- cases ---------------------------------------------------------------
  cases: '/cases',
  case: (id: string) => `/cases/${id}`,
  caseTransition: (id: string) => `/cases/${id}/transition`,
  caseAvailableTransitions: (id: string) => `/cases/${id}/available-transitions`,
  /** The whole path for staff, what this person may skip or correct, and a suggestion. */
  caseStageOverview: (id: string) => `/cases/${id}/stage-overview`,
  /** POST {to_stage, reason}: skip ahead (lawyers, admins) or correct back (admins, the
   *  responsible lawyer). Always with a reason. */
  caseMoveStage: (id: string) => `/cases/${id}/move-stage`,
  caseTeam: (id: string) => `/cases/${id}/team`,
  caseTeamMember: (id: string, assignmentId: string) => `/cases/${id}/team/${assignmentId}`,
  caseResponsibleLawyer: (id: string) => `/cases/${id}/responsible-lawyer`,
  caseMilestones: (id: string) => `/cases/${id}/milestones`,
  caseMilestoneComplete: (id: string, milestoneId: string) =>
    `/cases/${id}/milestones/${milestoneId}/complete`,

  /** Nested resources. Staff root only — there is no portal registration for these. */
  caseNotes: (id: string) => `/cases/${id}/notes`,
  caseNote: (id: string, noteId: string) => `/cases/${id}/notes/${noteId}`,
  caseUpdates: (id: string) => `/cases/${id}/updates`,
  caseUpdate: (id: string, updateId: string) => `/cases/${id}/updates/${updateId}`,
  caseUpdatePublish: (id: string, updateId: string) =>
    `/cases/${id}/updates/${updateId}/publish`,
  caseTimeline: (id: string) => `/cases/${id}/timeline`,

  // -- work ----------------------------------------------------------------
  tasks: '/tasks',
  task: (id: string) => `/tasks/${id}`,
  /** Handing work to somebody notifies them, so it is an action, not a PATCH. */
  taskAssign: (id: string) => `/tasks/${id}/assign`,
  taskStatus: (id: string) => `/tasks/${id}/status`,
  taskCancel: (id: string) => `/tasks/${id}/cancel`,

  calendarEvents: '/calendar-events',
  calendarEvent: (id: string) => `/calendar-events/${id}`,
  calendarEventReschedule: (id: string) => `/calendar-events/${id}/reschedule`,
  calendarEventComplete: (id: string) => `/calendar-events/${id}/complete`,
  calendarEventCancel: (id: string) => `/calendar-events/${id}/cancel`,

  criticalDates: '/critical-dates',
  criticalDate: (id: string) => `/critical-dates/${id}`,
  /** Independent confirmation by a second person. Never the person who set it. */
  criticalDateVerify: (id: string) => `/critical-dates/${id}/verify`,
  criticalDateSatisfy: (id: string) => `/critical-dates/${id}/satisfy`,
  criticalDateSupersede: (id: string) => `/critical-dates/${id}/supersede`,
  /** `?days=` horizon. */
  criticalDatesUpcoming: '/critical-dates/upcoming',
  /** Missed, inside the seven-day rung, or unverified. The morning risk view. */
  criticalDatesAtRisk: '/critical-dates/at-risk',

  // -- documents -----------------------------------------------------------
  documents: '/documents',
  document: (id: string) => `/documents/${id}`,
  documentVersions: (id: string) => `/documents/${id}/versions`,
  documentReview: (id: string) => `/documents/${id}/review`,
  documentVisibility: (id: string) => `/documents/${id}/visibility`,
  documentDownload: (id: string) => `/documents/${id}/download`,
  documentRequestReplacement: (id: string) => `/documents/${id}/request-replacement`,
  documentUploadAuthorization: '/documents/upload-authorization',
  documentRegisterUpload: '/documents/register-upload',

  documentRequests: '/document-requests',
  documentRequest: (id: string) => `/document-requests/${id}`,
  documentRequestsBulk: '/document-requests/bulk',
  /** The firm's own categories. What a client can be asked for is configuration. */
  documentCategories: '/document-categories',
  documentRequestCancel: (id: string) => `/document-requests/${id}/cancel`,
  documentRequestRemind: (id: string) => `/document-requests/${id}/remind`,

  // -- agreements ----------------------------------------------------------
  /**
   * No PUT or PATCH exists on this resource. Once signed an agreement is
   * immutable and no role, including Organization Administrator, may alter it —
   * amendments create a new version. Every verb below is a named command.
   *
   * `DELETE agreement(id)` with `{ reason }` deletes a draft or withdraws an issued,
   * unsigned letter (`agreement.delete`); a signed one is refused with a 409.
   */
  agreements: '/agreements',
  agreement: (id: string) => `/agreements/${id}`,
  /** Add a signatory. Draft only: recipients are fixed once the agreement is sent. */
  agreementRecipients: (id: string) => `/agreements/${id}/recipients`,
  /**
   * Send ONE signatory their link again. Requires `Idempotency-Key` — a retry after a
   * timeout must not be a second copy in somebody's inbox.
   *
   * It does not invalidate the link they already hold. Link tokens are stored hashed, so
   * the original cannot be re-sent literally; a second is minted and the first is left
   * alone, and both open the same document as the same signatory. Refused with a 409 on
   * a draft (nothing has been sent at all yet), on somebody who has already signed, and
   * on a signatory with no address.
   */
  agreementResend: (id: string, recipientId: string) =>
    `/agreements/${id}/recipients/${recipientId}/resend`,
  /** Issue for signature, FREEZING the text. Requires `Idempotency-Key`. */
  agreementSend: (id: string) => `/agreements/${id}/send`,
  /**
   * Record that a named signatory executed it — signed in the office, or returned a
   * wet-signed copy. Requires `Idempotency-Key`, and the recipient is always named:
   * "whoever is next" is how a joint client's signature is attributed to the wrong
   * spouse. Idempotent server-side.
   */
  agreementExecution: (id: string) => `/agreements/${id}/execution`,
  /**
   * The firm's own signature, which makes the agreement active. Refused with a 409
   * until every required signatory has accepted — countersigning something the client
   * has not signed asserts a bilateral agreement that does not exist.
   */
  agreementCountersign: (id: string) => `/agreements/${id}/countersign`,
  /** Where it has got to, and — the useful half — who it is waiting on. */
  agreementStatus: (id: string) => `/agreements/${id}/status`,
  /** The signature evidence package, newest first. Returns a bare array. */
  agreementEvidence: (id: string) => `/agreements/${id}/evidence`,
  /**
   * The picker `SendEngagementDialog` reads: active templates only, and gated on
   * `agreement.view` rather than `template` — anyone who can already see agreements may
   * read the name of a letter they are allowed to send. Returns a bare array, like
   * `agreementEvidence` above.
   */
  agreementTemplateOptions: '/agreements/templates',

  // -- agreement templates ---------------------------------------------------
  /**
   * Managing the firm's own engagement-letter templates: create, edit, list, archive.
   * Gated on the `template` capability — settings-level configuration, held only by the
   * Organization Administrator role today. The drafting picker is `agreementTemplateOptions`
   * above, a different, more widely-held capability.
   */
  agreementTemplates: '/agreement-templates',
  agreementTemplate: (id: string) => `/agreement-templates/${id}`,

  // -- finance -------------------------------------------------------------
  invoices: '/invoices',
  invoice: (id: string) => `/invoices/${id}`,
  invoiceLines: (id: string) => `/invoices/${id}/lines`,
  invoiceLine: (id: string, lineId: string) => `/invoices/${id}/lines/${lineId}`,
  /** Requires `Idempotency-Key`. */
  invoiceIssue: (id: string) => `/invoices/${id}/issue`,
  invoiceSend: (id: string) => `/invoices/${id}/send`,
  invoiceCreditNotes: (id: string) => `/invoices/${id}/credit-notes`,

  payments: '/payments',
  payment: (id: string) => `/payments/${id}`,
  /**
   * The most consequential route in the product. Two-person control, and a duplicate
   * does lasting damage — an over-credited invoice looks settled and stops being
   * chased. `Idempotency-Key` is REQUIRED, not optional.
   */
  paymentVerify: (id: string) => `/payments/${id}/verify`,
  paymentReject: (id: string) => `/payments/${id}/reject`,
  paymentReceipt: (id: string) => `/payments/${id}/receipt`,

  creditNotes: '/credit-notes',
  receipts: '/receipts',
  receipt: (id: string) => `/receipts/${id}`,
  refunds: '/refunds',
  /** Requires `Idempotency-Key`. Org Admin only — money leaving needs a second person. */
  refundApprove: (id: string) => `/refunds/${id}/approve`,

  unallocatedReceipts: '/unallocated-receipts',
  unallocatedReceiptsSuggestions: '/unallocated-receipts/suggestions',
  unallocatedReceiptAllocate: (id: string) => `/unallocated-receipts/${id}/allocate`,

  /** Per-client money position. Read-only. */
  financialPosition: '/financial-position',
  clientFinancialPosition: (clientId: string) => `/financial-position/${clientId}`,

  // -- NOT YET BUILT: served by MSW until the owning agent reports ----------
  leads: '/leads',
  lead: (id: string) => `/leads/${id}`,
  leadActivities: (id: string) => `/leads/${id}/activities`,
  leadNotes: (id: string) => `/leads/${id}/notes`,
  leadAssign: (id: string) => `/leads/${id}/assign`,
  leadStage: (id: string) => `/leads/${id}/stage`,
  /**
   * What converting this lead would create, and every reason it cannot yet.
   * Read by the review screen before anything is created.
   */
  /**
   * Which matters this enquiry would become. Set when it is qualified, which is what
   * lets conversion be automatic later: signing says THAT somebody engaged, not what
   * to open.
   *
   * The body is the whole list — it replaces the set rather than adding to it.
   */
  leadServices: (id: string) => `/leads/${id}/services`,
  leadConversionPlan: (id: string) => `/leads/${id}/conversion-plan`,
  /**
   * Creates a client, its contacts and its matters from a signed engagement.
   * Requires `Idempotency-Key` — a double-submitted form must not produce two clients.
   */
  leadConvert: (id: string) => `/leads/${id}/convert`,
  leadSources: '/lead-sources',
  leadStages: '/lead-stages',
  /**
   * Bringing a pipeline across from another system. The file is sent as text in a
   * JSON body, not multipart. `POST` with `dry_run: true` first — it validates the
   * whole file and writes nothing, which is what turns a failed import from a mystery
   * into a list of rows to fix.
   */
  leadImport: '/leads/import',
  /** The columns THIS firm's file may carry, including its own custom fields. */
  leadImportTemplate: '/leads/import/template',
  /**
   * The fields this firm added to leads, for DRAWING THE FORM. Read-only, and gated
   * on `lead.view` rather than `organization.configure` — a paralegal has to be able
   * to fill a field in without being able to create one. Manage them through
   * `customFields`, which is the administrator's surface.
   */
  leadCustomFields: '/leads/custom-fields',
  followUps: '/follow-ups',

  conflictChecks: '/conflict-checks',
  conflictCheck: (id: string) => `/conflict-checks/${id}`,
  //: A check is produced by a search and closed by a decision. Neither is a PATCH --
  //: both are sub-resources that call the service, so a caller cannot record a cleared
  //: check simply by asking for one.
  conflictCheckDecide: (id: string) => `/conflict-checks/${id}/decide`,
  conflictCheckMatches: (id: string) => `/conflict-checks/${id}/matches`,
  /**
   * LIVE on the backend (`domains/communications`), unlike its neighbours above. The
   * MSW handler that answered an empty page here has been deleted: it shadowed the real
   * list, so a thread created against Django disappeared on the next refresh.
   */
  conversations: '/conversations',
  /** Read the thread or add to it. GET pages newest-first, so a long conversation never
   *  has to be fetched whole to show the end of it. */
  conversationMessages: (id: string) => `/conversations/${id}/messages`,
  conversationRead: (id: string) => `/conversations/${id}/read`,
  /** Colleagues you may message. Gated on `conversation.communicate`, not `user.view`:
   *  administering people and addressing them are different questions, and only
   *  administrators and partners hold the former. */
  conversationsAddressable: '/conversations/addressable',
  notifications: '/notifications',
  /** Clears the bell. Marks only the caller's own unread notifications: the endpoint has
   *  no capability behind it, and the recipient filter is the whole authorization. */
  notificationsReadAll: '/notifications/read-all',
  /** Unread counts per navigation area, for the dots in the sidebar. */
  notificationsSummary: '/notifications/summary',
  /** Clears one area's dot. Visiting Cases says nothing about your invoices, so this
   *  takes the area and clears only its own. */
  notificationsReadArea: '/notifications/read-area',

  /**
   * The firm's own matter types. Read by everybody who touches a matter; written only by
   * whoever holds `service.edit` — the catalogue decides what the firm sells, how each
   * matter runs and when work is locked.
   *
   * Pass `is_active=true` wherever somebody is CHOOSING one. A retired matter type stays
   * readable by identifier, because a matter already under it has to be able to name it,
   * and it must never appear in a chooser.
   */
  services: '/services',
  service: (id: string) => `/services/${id}`,
  /**
   * Take a matter type out of use. NOT a delete: matters point at it, invoices reach it
   * through them, and the name is what a client reads on their own file. Refused with a
   * 409 while live matters sit under it.
   */
  serviceRetire: (id: string) => `/services/${id}/retire`,
  serviceReinstate: (id: string) => `/services/${id}/reinstate`,
  /** The work gates, each with the one line explaining it. Served so the explanation of
   *  a rule lives beside the rule rather than in a second copy in this app. */
  serviceWorkGates: '/services/work-gates',
  workflows: '/workflows',
  workflow: (id: string) => `/workflows/${id}`,
  /** One stage, PATCHed for what the CLIENT reads about it: `client_title` (the step's
   *  name in their progress tracker) and `client_label` (the sentence). Nothing else on a
   *  stage is editable. Administrators only (`workflow.edit`). */
  workflowStage: (id: string) => `/workflow-stages/${id}`,

  // -- clients: LIVE on the backend (domains/clients/urls.py) --------------
  /**
   * A client is ARCHIVED, never deleted — the firm's retention obligation outlives
   * the relationship — so there is no DELETE on this resource at all.
   */
  clients: '/clients',
  client: (id: string) => `/clients/${id}`,
  /** Same shape as `leadImport`. Dry run first. */
  clientImport: '/clients/import',
  clientImportTemplate: '/clients/import/template',
  /** As `leadCustomFields`. Gated on `client.view`. */
  clientCustomFields: '/clients/custom-fields',
  clientArchive: (id: string) => `/clients/${id}/archive`,
  /**
   * Opens the portal door for ONE contact. It grants sight of nothing on its own —
   * what they can read is decided per matter on `contactMatterAccess`.
   * Requires `Idempotency-Key`: a retry after a timeout must not invite twice.
   */
  clientInvitePortal: (id: string) => `/clients/${id}/invite-portal`,

  clientContacts: (id: string) => `/clients/${id}/contacts`,
  clientContact: (id: string, contactId: string) => `/clients/${id}/contacts/${contactId}`,
  /** Promotes this contact and demotes the incumbent in one transaction. */
  clientContactPrimary: (id: string, contactId: string) =>
    `/clients/${id}/contacts/${contactId}/primary`,
  /** DELETE only. Revokes every grant this contact holds, across every matter. */
  clientContactPortalAccess: (id: string, contactId: string) =>
    `/clients/${id}/contacts/${contactId}/portal-access`,

  /**
   * GET the whole consent history, POST to capture a new decision.
   * There is no PATCH and no DELETE anywhere on consents: a withdrawal is a NEW row,
   * because the original is the evidence that consent was validly held at the time.
   */
  clientConsents: (id: string) => `/clients/${id}/consents`,
  clientConsentWithdraw: (id: string, consentId: string) =>
    `/clients/${id}/consents/${consentId}/withdraw`,

  /**
   * Per-matter portal access for one contact (CR-21). Hangs off the CONTACT, not the
   * client: one client's three contacts can legitimately have three different views
   * of the same matter. This table is the portal's scope resolver and is never
   * cached, so a revocation takes effect on the client's very next request.
   */
  contactMatterAccess: (contactId: string) => `/contacts/${contactId}/matter-access`,
  contactMatterAccessGrant: (contactId: string, accessId: string) =>
    `/contacts/${contactId}/matter-access/${accessId}`,

  // -- firm administration: LIVE (domains/orgconfig/urls.py) ---------------
  /**
   * The firm's own people. Membership rows are never deleted — who held which role,
   * and when, is the resolution path for every historical authorization question —
   * so a member who has gone is SUSPENDED and the row stays.
   */
  users: '/users',
  user: (id: string) => `/users/${id}`,
  /** Needs `role.edit` on top of `user.edit`. Administration and access control are
   *  separate decisions and separate grants. */
  userRole: (id: string) => `/users/${id}/role`,
  userSuspend: (id: string) => `/users/${id}/suspend`,
  userReinstate: (id: string) => `/users/${id}/reinstate`,
  /** Ends the membership and stamps `left_at`. POST, not DELETE: the row survives,
   *  because who held which role and when has to stay answerable. */
  userRemove: (id: string) => `/users/${id}/remove`,

  /** Read-only. Roles are platform-owned in this phase (CR-31). */
  roles: '/roles',

  departments: '/departments',
  department: (id: string) => `/departments/${id}`,

  /**
   * The fields this firm added to leads and clients for itself. `?entity=lead` or
   * `?entity=client` to narrow.
   *
   * Gated on `organization.configure` for every verb INCLUDING read — the list is a
   * description of how the firm has shaped its records, which is configuration.
   * A DELETE is soft and leaves the stored values alone, so a field removed by
   * mistake comes back with its data.
   */
  customFields: '/custom-fields',
  customField: (id: string) => `/custom-fields/${id}`,

  offices: '/offices',
  office: (id: string) => `/offices/${id}`,

  /**
   * The firm's own record. NO IDENTIFIER IN THE URL — the row is resolved from the
   * bound tenant, so there is no path by which a caller could ask for another firm's
   * profile. Registered with `add_path` but on the staff root, so it is requested
   * like any other resource; the client's trailing slash is correct here.
   */
  organization: '/organization',
  /**
   * The address this firm's client email goes out as.
   *
   * GET reads it, POST sets it and sends a confirmation link to the address itself —
   * which is the only proof of ownership that means anything. `organization.configure`
   * for every verb: this decides where a client's reply about their own matter lands.
   */
  organizationEmailIdentity: '/organization/email-identity',
  organizationEmailIdentityConfirm: '/organization/email-identity/confirm',
  /**
   * The firm's own Gmail account, connected once via OAuth and used for every
   * subsequent send. `organization.configure` for every verb, same as the identity
   * screen above. GET reads the connection (or `{connection: null}`); DELETE
   * disconnects. POST is not here — `organizationGmailAuthorize` starts that flow.
   */
  organizationGmail: '/organization/gmail',
  /**
   * POST returns `{authorize_url}` to redirect the browser to — it does not redirect
   * itself. This is called from a fetch carrying the staff session's Authorization
   * header, which a top-level navigation to accounts.google.com could never send.
   */
  organizationGmailAuthorize: '/organization/gmail/authorize',
  /** PUT, not PATCH: branding is rendered as a set, and a half-updated set would put
   *  a stale logo beside a new colour scheme on a client-facing surface. */
  organizationBranding: '/organization/branding',
  /** The bank details clients are shown with every invoice. GET needs
   *  `organization.view`, PUT (the whole set) `organization.edit`. */
  organizationPaymentDetails: '/organization/payment-details',
} as const;

/**
 * `Idempotency-Key` is REQUIRED on these, not optional (Step 3 Spec §5). Omitting it
 * is a 400 — and where the backend is lenient it is still a client bug that shows up
 * as a duplicate payment.
 */
export const IDEMPOTENT_OPERATIONS = [
  'paymentVerify',
  'invoiceIssue',
  // `_require_idempotency_key` in `InvoiceViewSet.send` refuses a request without one:
  // a retry after a timeout must not re-emit `InvoiceSent`/`PaymentRequested` and mail
  // the client the same bill twice.
  'invoiceSend',
  'refundApprove',
  'documentRegisterUpload',
  'leadConvert',
  // `ClientViewSet.invite_portal` rejects a request without one outright: the action
  // reaches a client outside the application, and a retry after a timeout must not
  // invite the same person into the portal twice.
  'clientInvitePortal',
  // Issuing an agreement puts a document in somebody's hands and executing one records
  // a signature. `_require_idempotency_key` in `domains/agreements/views.py` refuses
  // either without a key: a double send is a second copy of a contract in the post.
  'agreementSend',
  'agreementExecution',
] as const;

/** A fresh key per user-initiated attempt. Reused verbatim when retrying that attempt. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

/**
 * The client portal's routes. Relative to `API_ROOTS.portal`.
 *
 * ===========================================================================
 * WHAT IS ABSENT HERE IS THE POINT
 * ===========================================================================
 * Twenty-two operations against roughly two hundred on staff. There is no route for
 * leads, conflict checks, critical dates, case team history, internal notes, audit
 * entries, unallocated receipts, or another client — not filtered, ABSENT. If a screen
 * seems to need one, it is a staff screen.
 *
 * Two rules that are enforced by the server and must also be respected here:
 *
 *   A matter's stage renders from `status`, which is the stage's `client_label`. The
 *   internal stage key and name are not on any portal payload at all.
 *
 *   A rejected document carries `client_reason` and never `internal_reason`. The
 *   latter is structurally absent from every portal serializer.
 */
export const PORTAL = {
  /** Firm name, the client's name, and which sections this firm has switched on. */
  config: '/config',
  /** The signed-in contact. */
  me: '/me',
  /**
   * The landing screen, assembled server-side and ordered deliberately: what you need
   * to do, then where things stand, then what happens next.
   */
  dashboard: '/dashboard',

  cases: '/cases',
  case: (id: string) => `/cases/${id}`,
  /**
   * The client's view of the activity stream. Built from the SAME timeline rows the
   * firm reads, rendered through the event registry's client wording — an event with
   * no client wording has nothing to render and so cannot appear.
   */
  caseTimeline: (id: string) => `/cases/${id}/timeline`,

  /** What the firm is still waiting for. The app leads with this. */
  documentRequests: '/document-requests',
  documents: '/documents',
  /** Refused unless the file has been scanned clean. */
  documentDownload: (id: string) => `/documents/${id}/download`,

  invoices: '/invoices',
  /** The client tells the firm they have paid. It verifies nothing on its own. */
  paymentProof: (id: string) => `/payments/${id}/proof`,

  agreements: '/agreements',
  agreementAccept: (id: string) => `/agreements/${id}/accept`,

  updates: '/updates',
} as const;

/**
 * The link audience. Relative to `API_ROOTS.link`.
 *
 * ===========================================================================
 * THREE OPERATIONS, AND THAT IS THE WHOLE SURFACE
 * ===========================================================================
 * A link holder is an outsider by construction: no account, no membership, no
 * capability at any scope. Their entire authority is the binding on their session,
 * so the surface they can reach is one object and two verbs against it.
 *
 * Every refusal here is a 404 with no detail, including "that agreement is real but
 * not yours". Telling the holder of an emailed credential that an identifier exists
 * somewhere would be a disclosure to precisely the audience that must not have it.
 */
export const LINK = {
  agreement: (id: string) => `/agreements/${id}`,
  agreementAccept: (id: string) => `/agreements/${id}/accept`,
} as const;
